import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveColumnDefinitions, syncActions } from '../../../actions/dictionary-values';
import { getTenantList, updatePublishedMetadata } from '../../../actions/dictionary';
import history from '../../../history';
import { LOAD_STATES } from '../../../reducers/common';
import { commonEnums } from '@codametrix/ui-common';
import { BreadCrumbsComponent } from '../../../components/breadcrumbs/breadcrumbsComponent';
import { DataGrid } from '../../../components/data-grid/data-grid';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Flex, FormControl, FormHelperText, Grid, Text, Tooltip } from '@chakra-ui/react';
import { styles } from '../dictionary-styles';
import { Form } from '../form';
import IconLabel from '../../../components/icon-label/iconlabel';
import RequestErrors from '../request-errors/request-errors';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import { Box } from '@chakra-ui/react';
const classes = styles();
const { DictionaryStatus } = commonEnums;
const UTF8_BOM_REGEX = /^\uFEFF/;
const CROSS_MAPS_ROUTE_MARKER = '/metadata/crossMaps';
const normalizeColumnName = (value)=>(value || '').replace(UTF8_BOM_REGEX, '').trim();
const handleRowAction = (event, columnDefinition, dispatch)=>{
    event.stopPropagation();
    dispatch(syncActions.removeColumnMapping(columnDefinition));
};
export const getColumnDefs = (dispatch)=>{
    return [
        {
            field: 'name',
            headerName: 'Column Name'
        },
        {
            field: 'keyColumnIndicator',
            headerName: 'Key Column Indicator'
        },
        {
            field: 'cellReference.organization',
            headerName: 'Mapped Organization Name',
            cellRenderer: (params)=>{
                const { data = {} } = params;
                if (data.cellReferences?.length) {
                    if (data.cellReferences[0].parentOrganizationName) {
                        return /*#__PURE__*/ _jsxs(Grid, {
                            display: "inline-flex",
                            alignItems: "center",
                            children: [
                                /*#__PURE__*/ _jsx(Tooltip, {
                                    label: "Inherited from CodaMetrix",
                                    placement: "bottom",
                                    children: /*#__PURE__*/ _jsx(BiomeIcon, {
                                        iconName: IconName.Info,
                                        iconSize: "16",
                                        iconClass: `${classes.infoIcon}`
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Text, {
                                    sx: classes.orgName,
                                    children: data.cellReferences[0].organizationName
                                })
                            ]
                        });
                    }
                    return data.cellReferences[0].organizationName;
                }
                return '';
            }
        },
        {
            field: 'cellReference.dictionaryName',
            headerName: 'Mapped Dictionary Name',
            cellRenderer: (params)=>{
                const { data = {} } = params;
                if (data.cellReferences?.length) {
                    return data.cellReferences[0].dictionaryName;
                }
                return '';
            }
        },
        {
            field: 'cellReference.lookupProperty',
            headerName: 'Mapped Property',
            cellRenderer: (params)=>{
                const { data = {} } = params;
                if (data.cellReferences?.length) {
                    return data.cellReferences[0].lookupProperty;
                }
                return '';
            }
        },
        {
            field: 'cellReference.isRefValueList',
            headerName: 'Multiple Values',
            cellRenderer: (params)=>{
                const { data = {} } = params;
                if (data.cellReferences?.length) {
                    return data.cellReferences[0]?.isRefValueList ? /*#__PURE__*/ _jsx(Grid, {
                        sx: classes.columnIcon,
                        children: /*#__PURE__*/ _jsx(Checkbox, {
                            isChecked: true
                        })
                    }) : /*#__PURE__*/ _jsx(Grid, {
                        sx: classes.columnIcon,
                        children: /*#__PURE__*/ _jsx(Checkbox, {
                            isChecked: false
                        })
                    });
                }
                return /*#__PURE__*/ _jsx(Grid, {
                    sx: classes.columnIcon,
                    children: /*#__PURE__*/ _jsx(Checkbox, {
                        isChecked: false
                    })
                });
            }
        },
        {
            field: 'remove',
            headerName: 'Clear mapping',
            action: 'remove',
            cellRenderer: (params)=>{
                const { data = {} } = params;
                return /*#__PURE__*/ _jsx(Grid, {
                    sx: classes.columnIcon,
                    "data-testid": "clear-mapping",
                    onClick: (event)=>{
                        handleRowAction(event, data, dispatch);
                    },
                    children: /*#__PURE__*/ _jsx(BiomeIcon, {
                        iconName: IconName.Cancel_Circle,
                        iconClass: `${classes.removeIcon}`,
                        titleText: "Remove"
                    })
                });
            }
        },
        {
            field: 'cellReference.textEditable',
            headerName: 'Text Box Editable',
            cellRenderer: (params)=>{
                const { data = {} } = params;
                if (data.cellReferences?.length) {
                    return data.cellReferences[0]?.textEditable ? /*#__PURE__*/ _jsx(Grid, {
                        sx: classes.columnIcon,
                        children: /*#__PURE__*/ _jsx(Checkbox, {
                            isChecked: true
                        })
                    }) : /*#__PURE__*/ _jsx(Grid, {
                        sx: classes.columnIcon,
                        children: /*#__PURE__*/ _jsx(Checkbox, {
                            isChecked: false
                        })
                    });
                }
                return /*#__PURE__*/ _jsx(Grid, {
                    sx: classes.columnIcon,
                    children: /*#__PURE__*/ _jsx(Checkbox, {
                        isChecked: false
                    })
                });
            }
        }
    ];
};
const crossMapSelectFormButtons = [
    {
        buttonType: 'submit',
        buttonText: 'Submit'
    },
    {
        buttonType: 'link',
        buttonText: 'back'
    }
];
const crossMapSelectForm = {
    buttons: crossMapSelectFormButtons
};
const determineStatus = (sortablePageable)=>{
    return sortablePageable.filterableOptions.filters?.find((filter)=>filter.key === 'status')?.terms || [
        DictionaryStatus.PUBLISHED
    ];
};
const CrossMapSelect = (props)=>{
    const { viewOnly } = props;
    const { columnMetadataEditor: columnEditorState, selectedDictionary, sortablePageable, updatedRows } = useSelector((state)=>{
        return state.dictionaryValues;
    });
    const [openModal, setOpenModal] = useState(columnEditorState.loadState !== LOAD_STATES.failed);
    useEffect(()=>{
        if (columnEditorState.loadState !== LOAD_STATES.failed) {
            setOpenModal(false);
        } else {
            setOpenModal(true);
        }
    }, [
        columnEditorState.loadState
    ]);
    const handleModalClose = ()=>{
        setOpenModal(false);
    };
    const orgId = useSelector((state)=>{
        return state.ui.context.activeContext?.organizationId ?? -1;
    });
    const colErrors = useSelector((state)=>{
        return state.dictionaryValues.colErrors;
    });
    const dictionaryId = selectedDictionary.id;
    const dispatch = useDispatch();
    const handleRowSelection = (dataItem)=>{
        const targetColumnId = dataItem?.api?.getFocusedCell()?.column.getColId();
        if (viewOnly || targetColumnId === 'remove') {
            return;
        }
        const columnName = normalizeColumnName(dataItem?.data?.name);
        if (!columnName) {
            return;
        }
        const currentPath = window.location.pathname.replace(/\/+$/, '');
        const crossMapsMarkerIndex = currentPath.indexOf(CROSS_MAPS_ROUTE_MARKER);
        const basePath = crossMapsMarkerIndex >= 0 ? currentPath.slice(0, crossMapsMarkerIndex + CROSS_MAPS_ROUTE_MARKER.length) : currentPath;
        const nextPath = `${basePath}/${encodeURIComponent(columnName)}/edit`;
        if (currentPath === nextPath) {
            return;
        }
        dispatch(getTenantList(orgId));
        dispatch(syncActions.selectActiveColumn({
            ...dataItem.data,
            name: columnName
        }));
        history.push(nextPath);
    };
    const handleCrossMapSave = ()=>{
        const pathParts = window.location.pathname.split('/');
        pathParts.pop();
        pathParts.pop();
        const status = determineStatus(sortablePageable)[0];
        const successPath = `${pathParts.join('/')}`;
        if (status === DictionaryStatus.PUBLISHED && updatedRows) {
            dispatch(updatePublishedMetadata({
                dictionary: {
                    ...selectedDictionary,
                    status: DictionaryStatus.DRAFT,
                    rows: updatedRows,
                    columnDefinitions: columnEditorState.columnMetadata
                },
                successPath: successPath
            }));
        } else {
            const payload = {
                dictionaryId,
                succesPath: successPath,
                columnDefinitions: columnEditorState.columnMetadata
            };
            dispatch(saveColumnDefinitions(payload));
        }
    };
    const handleAction = (event)=>{
        const { detail } = event;
        if (detail.eventType === 'cancel') {
            if (viewOnly) {
                history.goBack();
            } else {
                const pathParts = window.location.pathname.split('/');
                const newRoute = pathParts.splice(pathParts.length);
                newRoute.push('keyColumn');
                history.push(`${newRoute.join('/')}`);
            }
        }
    };
    const formEnabler = ()=>{
        return !viewOnly;
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Flex, {
                children: /*#__PURE__*/ _jsx(BreadCrumbsComponent, {})
            }),
            /*#__PURE__*/ _jsx(RequestErrors, {
                errors: colErrors,
                openModal: openModal,
                handleClose: handleModalClose
            }),
            /*#__PURE__*/ _jsx(Box, {
                overflowY: "auto",
                children: /*#__PURE__*/ _jsx(Form, {
                    formDefinition: crossMapSelectForm,
                    onSave: handleCrossMapSave,
                    onAction: handleAction,
                    enable: formEnabler,
                    title: 'Column Metadata - Cross Maps',
                    dataItem: {},
                    errors: {
                        status: '',
                        message: null,
                        errors: [],
                        fieldErrors: {}
                    },
                    inline: false,
                    children: /*#__PURE__*/ _jsxs("fieldset", {
                        style: classes.fieldset,
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                style: classes.legend,
                                children: "Cross Maps"
                            }),
                            /*#__PURE__*/ _jsxs(Grid, {
                                sx: classes.datagrid,
                                children: [
                                    /*#__PURE__*/ _jsx(DataGrid, {
                                        rowData: columnEditorState.columnMetadata,
                                        columnDefs: getColumnDefs(dispatch),
                                        onRowClicked: (data)=>handleRowSelection(data)
                                    }),
                                    /*#__PURE__*/ _jsx(FormControl, {
                                        children: /*#__PURE__*/ _jsx(FormHelperText, {
                                            children: /*#__PURE__*/ _jsx(IconLabel, {
                                                isLeft: true,
                                                label: 'Select a column name from the table above to add a cross map'
                                            })
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                })
            })
        ]
    });
};
CrossMapSelect.displayName = 'CrossMapSelect';
export { CrossMapSelect };
